import React, { useState }       from 'react';
import DV                        from 'ts-data-validator';
import ReactPaginate             from 'react-paginate';
import { SessionStorageService } from 'ts-storage-service';

import PromotionsItem from './PromotionsItem';
import Notfound       from '../../notfound/components/Notfound';
import Meta           from '../../../components/meta';
import PageHeader     from '../../../components/pageHeader';
import Preloader      from '../../../components/containers/preloader';

import { usePromotions }          from '../../../providers/promotions';
import { useCommon }              from '../../../providers/common';
import { useLocation }            from 'react-router-dom';
import TopPromotions              from './TopPromotions';
import PaginationPreviosArrowIcon from '../../../components/icons/PaginationPreviosArrowIcon';
import PaginationNextArrowIcon    from '../../../components/icons/PaginationNextArrowIcon';

const Promotions = function() {
  const { pages }                     = useCommon();
  const { pathname }                  = useLocation();
  const {
          getPromotionsList,
          showMore,
          initialStateLoaded,
          loading,
          ...promotions
        }                             = usePromotions();
  const ss                            = new SessionStorageService();
  const [savedPage, setSavedPage]     = useState(0);
  const [windowWidth, setWindowWidth] = useState(null);

  const getTopPromotionImg = React.useMemo(() => {
    const { headItems } = promotions;

    if (windowWidth <= 990 && headItems[0]?.image333x236) {
      return headItems[0].image333x236;
    } else if (headItems[0]?.image1000x346) {
      return headItems[0].image1000x346;
    } else {
      return headItems[0]?.headImage;
    }
  }, [windowWidth, promotions]);

  const getPromotionItemImg = React.useCallback((item) => {
    if (windowWidth <= 990 && item?.image333x236) {
      return item.image333x236;
    } else if (item?.image320x240) {
      return item.image320x240;
    } else {
      return item.previewImage;
    }
  }, [windowWidth]);

  const handleClick = React.useCallback(
    (page) => {
      const { limit } = promotions;
      showMore(page, limit);
      setSavedPage(page);
      ss.setData('savedPromotionsPage', page);

      window.scrollTo({
        top:      0,
        behavior: 'smooth',
      });
    },
    [promotions],
  );

  const handleResize = React.useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  React.useEffect(() => {
    setWindowWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);

    const savedPromotionsPage = ss.getData('savedPromotionsPage');
    if (savedPromotionsPage) {
      handleClick(savedPromotionsPage);
    } else {
      getPromotionsList();
      scrollTo({
        top:  0,
        left: 0,
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderPage = React.useCallback(
    (page) => {
      const {
              items,
              count,
              headItems = [],
            } = promotions;

      return (
        <main className={page.template}>
          <section className={'body'}>
            <div className={'container'}>
              {headItems.length > 0 && windowWidth && (
                <TopPromotions
                  activeFrom={headItems[0]?.activeFrom}
                  activeTo={headItems[0]?.activeTo}
                  announce={headItems[0]?.announce}
                  date={headItems[0]?.createdAt}
                  headImage={getTopPromotionImg}
                  image={headItems[0]?.previewImage}
                  label={headItems[0]?.label}
                  readingTime={headItems[0]?.readingTime}
                  showCounter={headItems[0]?.showCounter}
                  urlKey={headItems[0]?.urlKey}
                />)}
              <div className={'items_container'}>
                {items
                  .slice(-9)
                  .map((item, i) => {
                    return (
                      <PromotionsItem
                        activeFrom={item.activeFrom}
                        activeTo={item.activeTo}
                        date={item.createdAt}
                        image={getPromotionItemImg(item)}
                        key={i}
                        label={item.label}
                        readingTime={item.readingTime}
                        showCounter={item.showCounter}
                        urlKey={item.urlKey}
                      />
                    );
                  })
                }
              </div>
            </div>
          </section>
          <hr />
          <section className={'bottom'}>
            {count > 10 && (
              <ReactPaginate
                activeClassName={'active'}
                breakClassName={'page_item'}
                breakLabel={'...'}
                containerClassName={'pagination'}
                forcePage={savedPage}
                marginPagesDisplayed={2}
                nextLabel={
                  (
                    savedPage + 1) < (
                    (
                      count - 1) / 9) && <PaginationNextArrowIcon />
                }
                onPageChange={(currentPage) => {
                  handleClick(currentPage.selected);
                }}
                pageClassName={'page_item'}
                pageCount={Math.ceil((count - 1) / 9)}
                pageRangeDisplayed={3}
                previousLabel={
                  savedPage !== 0 && <PaginationPreviosArrowIcon />
                }
                renderOnZeroPageCount={null}
              />
            )}
          </section>
        </main>
      );
    },
    [promotions, windowWidth, savedPage, getTopPromotionImg, getPromotionItemImg, handleClick],
  );

  const page = React.useMemo(
    () =>
      pages.find(
        (page) => `/${page.urlKey}` === pathname,
      ),
    [pages, pathname],
  );

  if (!page) {
    return <Notfound />;
  }
  const {
          headImage,
          label,
          meta,
        } = page;

  const headerProps = {
    transparent: false,
  };

  if (DV.isString(headImage)) {
    headerProps.transparent = true;
    headerProps.bg          = headImage;
  }

  return (
    <>
      <div className={'promotions-page-content'}>
        <Meta {...meta} />
        {DV.isNotEmptyString(label) && (
          <PageHeader {...headerProps}>{label}</PageHeader>
        )}
        {renderPage(page)}
      </div>
      {(
        loading || promotions.loading) && <Preloader />}
    </>
  );
};

export default Promotions;